import { useEffect, useCallback, useRef, useState } from 'react';

interface UseFormGuardProps {
    isDirty: boolean;
    onSave?: () => Promise<void>;
    isLoading?: boolean;
}

interface UseFormGuardReturn {
    showConfirmModal: boolean;
    setShowConfirmModal: (show: boolean) => void;
    handleNavigate: () => void;
    handleStayOnPage: () => void;
    handleSaveAndContinue: () => void;
    pendingNavigation: string | null;
    setPendingNavigation: (path: string | null) => void;
    isProcessing: boolean;
}

export const useFormGuard = ({ 
    isDirty, 
    onSave,
    isLoading = false 
}: UseFormGuardProps): UseFormGuardReturn => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const isNavigatingRef = useRef(false);

    // Intercept browser navigation (back button, refresh, etc.)
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty && !isNavigatingRef.current) {
                e.preventDefault();
                e.returnValue = 'Você tem alterações não salvas. Deseja sair mesmo assim?';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    const handleNavigate = useCallback(() => {
        isNavigatingRef.current = true;
        setShowConfirmModal(false);
        setPendingNavigation(null);
        
        // Allow the parent component to handle navigation
        if (pendingNavigation && (window as any).__pendingNavigateCallback) {
            (window as any).__pendingNavigateCallback(pendingNavigation);
        }
    }, [pendingNavigation]);

    const handleStayOnPage = useCallback(() => {
        setShowConfirmModal(false);
        setPendingNavigation(null);
        isNavigatingRef.current = false;
    }, []);

    const handleSaveAndContinue = useCallback(async () => {
        if (!onSave) {
            handleNavigate();
            return;
        }

        setIsProcessing(true);
        
        try {
            await onSave();
            // After successful save, allow navigation
            isNavigatingRef.current = true;
            setShowConfirmModal(false);
            
            // Execute pending navigation
            if (pendingNavigation && (window as any).__pendingNavigateCallback) {
                (window as any).__pendingNavigateCallback(pendingNavigation);
            }
            
            setPendingNavigation(null);
        } catch (error) {
            console.error('Erro ao salvar:', error);
            // Stay on page if save fails
            isNavigatingRef.current = false;
        } finally {
            setIsProcessing(false);
        }
    }, [onSave, pendingNavigation, handleNavigate]);

    return {
        showConfirmModal,
        setShowConfirmModal,
        handleNavigate,
        handleStayOnPage,
        handleSaveAndContinue,
        pendingNavigation,
        setPendingNavigation,
        isProcessing: isProcessing || isLoading
    };
};